import { measureText } from './model';

/**
 * Column width fitting (see `sizing` in types.ts).
 *
 * - fixed: `width`, or a width the user dragged. Never changes.
 * - auto: no `sizing` (the original behaviour): natural width, and the spare
 *   width is shared, by natural width, among the auto columns that `grow`
 *   (long text), when no fill column is visible. Short columns (a check, a
 *   number, a date, a code narrower than its header) keep their natural width:
 *   shared out to everyone, a one-letter flag column got as much air as a name.
 *   A grid with no long text at all (a table of numbers) still shares it among
 *   all its auto columns, as before.
 * - content: natural width (header and widest drawn value). Never changes.
 * - fill: shares what the others leave. Too little room: fill columns shrink
 *   first, in proportion to what each can give up, down to their minimum; only
 *   below that does the grid scroll sideways.
 */

export interface SizeSpec {
  kind: 'fixed' | 'auto' | 'content' | 'fill';
  natural: number;
  /** Fill columns: the narrowest they get. */
  min: number;
  /** Auto columns: takes a share of the spare width (default true). */
  grow?: boolean;
}

export function fitWidths(specs: SizeSpec[], reserved: number, avail: number): number[] {
  const widths = specs.map((s) => s.natural);
  const fills = specs.map((s, i) => (s.kind === 'fill' ? i : -1)).filter((i) => i >= 0);

  if (fills.length) {
    if (avail <= 0) return widths;
    const fixedTotal = reserved + specs.reduce((s, _c, i) => (fills.includes(i) ? s : s + widths[i]), 0);
    const min = fills.map((i) => specs[i].min);
    const nat = fills.map((i, k) => Math.max(min[k], specs[i].natural));
    const sumMin = min.reduce((s, w) => s + w, 0);
    const sumNat = nat.reduce((s, w) => s + w, 0);
    const rest = avail - fixedTotal;
    let out: number[];
    if (rest >= sumNat) {
      const spare = rest - sumNat;
      out = nat.map((w) => w + Math.floor((spare * w) / sumNat));
    } else if (rest > sumMin) {
      const give = sumNat - sumMin;
      out = nat.map((w, k) => min[k] + Math.floor(((w - min[k]) * (rest - sumMin)) / give));
    } else {
      out = min;
    }
    // The last fill column takes the rounding, so the columns meet the right edge.
    const used = out.reduce((s, w) => s + w, 0);
    if (rest > sumMin && used < rest) out[out.length - 1] += rest - used;
    fills.forEach((i, k) => { widths[i] = out[k]; });
    return widths;
  }

  const all = specs.map((s, i) => (s.kind === 'auto' ? i : -1)).filter((i) => i >= 0);
  const growing = all.filter((i) => specs[i].grow !== false);
  // No column of long text (a table of numbers): every auto column shares, as before.
  const autos = growing.length ? growing : all;
  const spare = avail - reserved - widths.reduce((s, w) => s + w, 0);
  const autoTotal = autos.reduce((s, i) => s + widths[i], 0);
  if (spare > 0 && autoTotal > 0) {
    let given = 0;
    autos.forEach((i, k) => {
      const add = k === autos.length - 1 ? spare - given : Math.floor((spare * widths[i]) / autoTotal);
      widths[i] += add;
      given += add;
    });
  }
  return widths;
}

/* ------------------------------------------------------------------ */
/*  Text measuring, cached                                             */
/* ------------------------------------------------------------------ */

const cache = new Map<string, Map<string, number>>();
const CACHE_MAX = 50_000;
let cached = 0;

/**
 * Width of `text` as a cell draws it. `tabular`: number and date cells use
 * tabular figures, every digit as wide as "0".
 */
export function drawnWidth(text: string, font: string, tabular: boolean): number {
  const t = tabular ? text.replace(/\d/g, '0') : text;
  let m = cache.get(font);
  if (!m) {
    m = new Map();
    cache.set(font, m);
  }
  let w = m.get(t);
  if (w === undefined) {
    if (cached > CACHE_MAX) {
      cache.clear();
      cached = 0;
      m = new Map();
      cache.set(font, m);
    }
    w = measureText(t, font);
    m.set(t, w);
    cached++;
  }
  return w;
}

/** The widest of `texts` (distinct texts are measured once). */
export function widestText(texts: Iterable<string>, font: string, tabular: boolean): number {
  let w = 0;
  const seen = new Set<string>();
  for (const t of texts) {
    if (!t || seen.has(t)) continue;
    seen.add(t);
    w = Math.max(w, drawnWidth(t, font, tabular));
  }
  return w;
}
